import { useQueryClient } from "@tanstack/react-query";
import { ObjectId } from "bson";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { faCircleCheck, faPlus, faTrashCan } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Icon, Text, toast } from "@performyard/py-components";

import SelectedEmployees from "../../../../../../../components/EmployeeSelection/SelectedEmployees";
import Spacer from "../../../../../../../components/Spacer";
import Tooltip from "../../../../../../../components/Tooltip";

import { useSetters } from "../store/store";

import { useGetSurveyRespondents } from "../../../../../../../api/hooks/defs/surveys";
import { useUserSelectionCount } from "../../../../../../../api/hooks/defs/user-selection";
import { useSurvey } from "../../../hooks/useSurvey";

import { removeSurveyParticipants } from "../../../../../../../api/services/defs/internal-api/surveys";
import {
	RemoveUsersFromSelectionProps,
	UserSelectionMutationResponse,
	UserSelectionUsersResponse,
} from "../../../../../../../api/services/defs/user-selection";

import { SURVEY_PARTICIPANTS_CONFIG } from "../constants";

export const Participants = ({ surveyId }: { surveyId: string }) => {
	const { survey } = useSurvey({ surveyId });
	const setters = useSetters();
	const queryClient = useQueryClient();
	const selectionObjectId = survey?.participantSelection?.selectionObjectId;
	const [removingParticipant, setRemovingParticipant] = useState<{ id: string; name: string } | null>(null);
	const [isRemovalInProgress, setIsRemovalInProgress] = useState(false);
	const isRemovalInProgressRef = useRef(false);
	const hasInitiallyLoadedRef = useRef(false);

	const {
		data: selectionObjectCount,
		isLoading: isLoadingSelectionObjectCount,
		isFetching: isFetchingSelectionObjectCount,
		refetch: refetchSelectionObjectCount,
	} = useUserSelectionCount(selectionObjectId || "");

	const { data: respondents, isLoading: isLoadingRespondents } = useGetSurveyRespondents(surveyId);

	const handleClickEdit = () => {
		setters.setEditParticipants(true);
	};

	const handleRemoveSuccess = (data: UserSelectionMutationResponse) => {
		if (data.success) {
			toast.success(`${removingParticipant?.name || "Participant"} has been removed from this survey`);
			queryClient.invalidateQueries({ queryKey: ["user-selection-users", selectionObjectId], exact: false });
		} else {
			toast.error("Failed to remove participant");
		}
		setRemovingParticipant(null);
		setIsRemovalInProgress(false);
		isRemovalInProgressRef.current = false;
	};

	const handleRemoveError = () => {
		toast.error("Failed to remove participant");
		setRemovingParticipant(null);
		setIsRemovalInProgress(false);
		isRemovalInProgressRef.current = false;
	};

	/**
	 * Custom remove handler that routes through internal API to trigger ETL for published surveys.
	 * This wrapper matches the UseMutateFunction signature expected by SelectedEmployees.
	 */
	const customRemoveHandler = useCallback(
		({ users }: RemoveUsersFromSelectionProps) => {
			const userIds = users.map((id) => id.toString());

			removeSurveyParticipants(surveyId, userIds)
				.then(() => {
					handleRemoveSuccess({ success: true });
				})
				.catch(() => {
					handleRemoveError();
				});
		},
		[surveyId]
	);

	useEffect(() => {
		if (isLoadingSelectionObjectCount || isFetchingSelectionObjectCount || hasInitiallyLoadedRef.current) return;
		refetchSelectionObjectCount();
	}, [isLoadingSelectionObjectCount, isFetchingSelectionObjectCount, hasInitiallyLoadedRef.current]);

	useEffect(() => {
		if (!isLoadingSelectionObjectCount && !isFetchingSelectionObjectCount) {
			if (!hasInitiallyLoadedRef.current) {
				hasInitiallyLoadedRef.current = true;
				return;
			}
			if (selectionObjectCount?.count === 0) {
				setters.setEditParticipants(true);
			}
		}
	}, [
		selectionObjectCount,
		isLoadingSelectionObjectCount,
		isFetchingSelectionObjectCount,
		hasInitiallyLoadedRef.current,
	]);

	const getCustomColumnDefs = useCallback(
		({ loadingStates, actions }) => {
			const { removeUsersFromSelection } = actions;
			const { isRemovingUsersFromSelection } = loadingStates;
			return [
				{
					headerName: "",
					field: "actions",
					width: 40,
					cellRenderer: ({ data }: { data: UserSelectionUsersResponse["users"][number] }) => {
						const handleRemoveParticipant = (participantId: ObjectId) => {
							// Check if any removal is in progress - use ref for immediate synchronous check
							if (isRemovingUsersFromSelection || isRemovalInProgressRef.current) return;
							// Set both ref and state immediately to prevent race conditions
							isRemovalInProgressRef.current = true;
							setIsRemovalInProgress(true);
							setRemovingParticipant({ id: data._id, name: data.name });
							removeUsersFromSelection({
								selectionObjectID: selectionObjectId,
								users: [participantId],
							});
						};
						const isAnyRemovalInProgress = isRemovingUsersFromSelection || isRemovalInProgress;

						if (respondents?.some((id) => id.toString() === data._id)) {
							return (
								<Tooltip message="Survey Completed" offsetSize={4}>
									<Icon icon={faCircleCheck} size={16} color="green-5" />
								</Tooltip>
							);
						}
						return (
							<Tooltip message="Remove Participant" offsetSize={4}>
								<ButtonIcon
									icon={faTrashCan}
									size={16}
									disabled={isAnyRemovalInProgress}
									onClick={() => handleRemoveParticipant(new ObjectId(data._id))}
								/>
							</Tooltip>
						);
					},
				},
			];
		},
		[removingParticipant, isLoadingRespondents, respondents, selectionObjectId, isRemovalInProgress]
	);

	return (
		<>
			<Spacer height={2} />
			<Text size="paragraph-1" weight="semi-bold">
				Participants
			</Text>
			<Spacer height={2} />
			{selectionObjectId && hasInitiallyLoadedRef.current && (
				<SelectedEmployees
					selectionObjectID={selectionObjectId}
					customFieldsIdentifiers={["Group", "Manager"]}
					getCustomColumnDefs={getCustomColumnDefs}
					exclusionList={[]}
					filters={{}}
					height={SURVEY_PARTICIPANTS_CONFIG.UI_CONFIG.CONTAINER_HEIGHT}
					onRemoveSuccess={handleRemoveSuccess}
					onRemoveError={handleRemoveError}
					customRemoveHandler={customRemoveHandler}
				/>
			)}
			{survey?.status !== "closed" && (
				<div className="flex justify-end">
					<span className="w-fit">
						<ButtonStandard
							color="primary"
							size="medium"
							variant="solid"
							onClick={handleClickEdit}
							iconLeft={faPlus}
						>
							Add Participants
						</ButtonStandard>
					</span>
				</div>
			)}
		</>
	);
};
